import React, { forwardRef } from 'react';
import { Select, Spin } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useUnitsOptions } from '../hooks/useUnits';
import PropTypes from 'prop-types';

/**
 * UnitsSelect Component
 * Wrapper รอบ Ant Design Select สำหรับเลือกหน่วยนับ
 * รองรับ caching, error handling, และ refresh
 */
const UnitsSelect = forwardRef(({
  category = '',
  includeCode = true,
  valueField = 'id',
  sortBy = 'display_order',
  placeholder = 'เลือกหน่วยนับ',
  allowClear = true,
  showSearch = true,
  showRefresh = false,
  disabled = false,
  size = 'middle',
  style = {},
  className = '',
  onChange,
  onUnitChange, // callback เพิ่มเติมที่ส่งข้อมูล unit เต็ม
  ...restProps
}, ref) => {
  const {
    unitsOptions,
    loading,
    error,
    refreshUnits,
    getUnitName,
    getUnitCode,
    findUnitByValue,
    isEmpty
  } = useUnitsOptions({
    category,
    includeCode,
    valueField,
    sortBy
  });

  /**
   * Handle change event
   */
  const handleChange = (value, option) => {
    // เรียก onChange ปกติ
    if (onChange) {
      onChange(value, option);
    }

    // เรียก onUnitChange พร้อมข้อมูล unit เต็ม
    if (onUnitChange) {
      const unitData = findUnitByValue(value);
      //console.log('component array', unitData);
      onUnitChange(value, unitData, option);
    }
  };

  /**
   * Filter option สำหรับ search
   */
  const filterOption = (input, option) => {
    const searchText = input.toLowerCase();
    const label = option.label.toLowerCase();
    const code = option.code?.toLowerCase() || '';
    const unit = option.unit?.unit_name?.toLowerCase() || '';
    
    return label.includes(searchText) || 
           code.includes(searchText) || 
           unit.includes(searchText);
  };

  // กำหนด placeholder ตามสถานะ
  let currentPlaceholder = placeholder;
  if (loading) {
    currentPlaceholder = 'กำลังโหลด...';
  } else if (error) {
    currentPlaceholder = 'เกิดข้อผิดพลาด';
  } else if (isEmpty) {
    currentPlaceholder = category ? `ไม่มีหน่วยนับในหมวด ${category}` : 'ไม่มีข้อมูลหน่วยนับ';
  }

  return (
    <div className={`units-select-wrapper ${className}`} style={style}>
      <Select
        ref={ref}
        placeholder={currentPlaceholder}
        allowClear={allowClear}
        showSearch={showSearch}
        disabled={disabled || loading}
        size={size}
        loading={loading}
        options={unitsOptions}
        onChange={handleChange}
        filterOption={showSearch ? filterOption : false}
        notFoundContent={
          loading ? <Spin size="small" /> : 
          error ? <span style={{ color: '#ff4d4f' }}>โหลดข้อมูลไม่สำเร็จ</span> :
          'ไม่พบข้อมูล'
        }
        dropdownRender={(menu) => (
          <div>
            {menu}
            {(error || showRefresh) && (
              <div 
                style={{ 
                  padding: '8px 12px', 
                  borderTop: '1px solid #f0f0f0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  color: '#1890ff'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  refreshUnits();
                }}
              >
                <ReloadOutlined spin={loading} />
                <span>รีเฟรชข้อมูล</span>
              </div>
            )}
          </div>
        )}
        {...restProps}
      />
    </div>
  );
});

UnitsSelect.displayName = 'UnitsSelect';

UnitsSelect.propTypes = {
  // Units hook options
  category: PropTypes.string,
  includeCode: PropTypes.bool,
  valueField: PropTypes.oneOf(['id', 'unit_name']),
  sortBy: PropTypes.oneOf(['display_order', 'unit_name', 'category']),
  
  // Select props
  placeholder: PropTypes.string,
  allowClear: PropTypes.bool,
  showSearch: PropTypes.bool,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'middle', 'large']),
  style: PropTypes.object,
  className: PropTypes.string,
  
  // Custom props
  showRefresh: PropTypes.bool,
  onChange: PropTypes.func,
  onUnitChange: PropTypes.func
};

export default UnitsSelect;

// เอาออกมาใช้แยกได้
export { UnitsSelect };